<script setup lang="ts">
import { computed } from "vue";
import dayjs from "dayjs";
import Modal from "@/ui/components/modal.vue";
import { usePriceListImport } from "@/modules/booking_experts/composables/usePriceListImport";
import ChangedPriceRangeDTO = Modules.BookingExperts.Data.MasterPriceList.ChangedPriceRangeDTO;

const { priceListId, importId } = defineProps<{
	priceListId?: number;
	importId?: number;
}>();

const isDialogVisible = defineModel<boolean>({ default: false });

const { importItem, isLoading } = usePriceListImport(
	() => priceListId,
	() => importId,
	() => isDialogVisible.value
);

const formatDate = (value?: string | null) =>
	value ? dayjs(value).format("DD MMM YYYY HH:mm") : "-";

const formatDay = (value?: string | null) =>
	value ? dayjs(value).format("DD MMM YYYY") : "-";

const formatDuration = (seconds?: number | null) => {
	if (seconds === null || seconds === undefined) {
		return "-";
	}

	return seconds < 60
		? `${seconds}s`
		: `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

/** "3 of 12" while the managed prices are being pushed out. */
const managedProgress = computed(() => {
	if (!importItem.value || importItem.value.managed_periods_total === null) {
		return "Not managed";
	}

	return `${importItem.value.managed_periods_done} of ${importItem.value.managed_periods_total} periods`;
});

const details = computed(() => {
	if (!importItem.value) {
		return [];
	}

	return [
		{ label: "Status", value: importItem.value.status },
		{ label: "Prices imported", value: `${importItem.value.imported_count}` },
		{
			label: "Prices changed",
			value: `${importItem.value.changed_price_count}`,
		},
		{ label: "Managed update", value: managedProgress.value },
		{
			label: "Covered period",
			value: `${formatDay(importItem.value.imported_from)} – ${formatDay(
				importItem.value.imported_to
			)}`,
		},
		{ label: "Started at", value: formatDate(importItem.value.started_at) },
		{ label: "Finished at", value: formatDate(importItem.value.finished_at) },
		{
			label: "Duration",
			value: formatDuration(importItem.value.duration_in_seconds),
		},
	];
});

const rangeLabel = (range: ChangedPriceRangeDTO) =>
	range.from === range.to
		? formatDay(range.from)
		: `${formatDay(range.from)} – ${formatDay(range.to)}`;
</script>

<template>
	<Modal
		v-model:visible="isDialogVisible"
		title="Import Details"
		icon="fa-light fa-circle-info"
		hide-actions
	>
		<div v-if="isLoading" class="d-flex justify-center py-8">
			<v-progress-circular indeterminate color="primary" />
		</div>

		<template v-else-if="importItem">
			<v-alert
				v-if="importItem.error_message"
				type="error"
				class="mb-4"
				:text="importItem.error_message"
			/>

			<dl class="details-grid">
				<template v-for="detail in details" :key="detail.label">
					<dt>{{ detail.label }}</dt>
					<dd>{{ detail.value }}</dd>
				</template>
			</dl>

			<v-divider class="my-4" />

			<h4 class="mb-2">
				Changed periods ({{ importItem.changed_price_ranges.length }})
			</h4>
			<perfect-scrollbar
				class="ranges-scroll"
				:options="{ suppressScrollX: true }"
			>
				<v-list
					v-if="importItem.changed_price_ranges.length"
					density="compact"
					lines="one"
				>
					<v-list-item
						v-for="range in importItem.changed_price_ranges"
						:key="`${range.from}-${range.to}`"
						:title="rangeLabel(range)"
					>
						<template #prepend>
							<i class="fa-light fa-calendar-range mr-3" />
						</template>
						<template #append>
							<v-chip label density="comfortable">
								{{ range.changed_count }}
							</v-chip>
						</template>
					</v-list-item>
				</v-list>
				<div v-else class="text-medium-emphasis text-center py-6">
					No prices changed in this import
				</div>
			</perfect-scrollbar>
		</template>

		<div v-else class="text-medium-emphasis text-center py-8">
			Import not found
		</div>
	</Modal>
</template>

<style scoped lang="scss">
.details-grid {
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 1.5rem;
	row-gap: 0.5rem;

	dt {
		font-weight: 600;
		color: rgba(var(--v-theme-on-surface), 0.7);
	}

	dd {
		margin: 0;
	}
}

.ranges-scroll {
	height: 220px;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	border-radius: 4px;
}
</style>
